import React from 'react';
import { useNavigate } from 'react-router-dom';
import ContactLogo from '../components/shared/ContactLogo';
import HolographicBeams from '../components/ui/beams-background';

const IconArrowLeft = () => (
  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M19 12H5M12 5l-7 7 7 7"/>
  </svg>
);

const IconArrowRight = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14M12 5l7 7-7 7"/>
  </svg>
);

const IconLinkedIn = () => (
  <svg width="14" height="14" fill="currentColor" viewBox="0 0 24 24">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
);

const VALUES = [
  {
    title: 'Simplicidad primero',
    desc: 'Cada funcionalidad nueva pasa por la misma pregunta: hace la vida más fácil o solo más compleja. Preferimos quitar antes que agregar.',
  },
  {
    title: 'Seguridad de nivel empresarial',
    desc: 'Auditamos y reforzamos activamente la plataforma, desde el manejo de datos hasta el acceso a la API, para que confíes en cada perfil que compartes.',
  },
  {
    title: 'Decisiones basadas en datos',
    desc: 'Creemos que cada interacción profesional se puede medir. Por eso construimos analíticas reales en vez de métricas de vanidad.',
  },
  {
    title: 'Cercanía real',
    desc: 'Somos un equipo pequeño, y eso significa que hablas directamente con las personas que construyen el producto, no con un ticket perdido en una fila.',
  },
];

const FOUNDERS = [
  {
    name: 'Lucas Valdivia R.',
    role: 'CEO',
    photo: 'https://res.cloudinary.com/dfyzvch4i/image/upload/c_thumb,g_face,w_500,h_500,z_0.75/v1781152749/FOTO_LUCAS_ezis14.png',
    linkedin: 'https://www.linkedin.com/in/lucasvaldiviarios/',
  },
  {
    name: 'Tomás Valdivia R.',
    role: 'COO',
    photo: 'https://res.cloudinary.com/dfyzvch4i/image/upload/c_thumb,g_face,w_500,h_500,z_0.75/v1781152749/FOTO_TOMA%CC%81S_i4pq1h.jpg',
    linkedin: 'https://www.linkedin.com/in/tomasvaldiviar/',
  },
];

const SobreNosotros = () => {
  const navigate = useNavigate();

  return (
    <div className="sn-root">
      <div className="sn-bg" aria-hidden="true">
        <HolographicBeams density={22} speed={0.7} aberration={2} opacity={45} />
      </div>

      {/* NAV */}
      <header className="sn-nav">
        <div className="sn-nav-inner">
          <ContactLogo clickable/>
          <button onClick={() => navigate('/')} className="sn-btn-back">
            <IconArrowLeft/> Volver
          </button>
        </div>
      </header>

      {/* HERO */}
      <section className="sn-hero">
        <div className="sn-container">
          <p className="sn-eyebrow">Sobre nosotros</p>
          <h1 className="sn-h1">Construyendo el futuro del networking profesional.</h1>
          <p className="sn-lead">
            Contact es una plataforma de networking inteligente que combina tarjetas NFC,
            perfiles digitales y analíticas para ayudar a profesionales y empresas a
            compartir información, medir interacciones y convertir contactos en
            oportunidades reales.
          </p>
        </div>
      </section>

      {/* STORY */}
      <section className="sn-story">
        <div className="sn-container sn-story-grid">
          <p className="sn-section-label">Nuestra historia</p>
          <div className="sn-story-body">
            <h2 className="sn-h2">Nace de un problema que todos hemos vivido.</h2>
            <p className="sn-p">
              Una tarjeta de papel se pierde en el fondo de una billetera. Un contacto de
              LinkedIn nunca recibe seguimiento. Una reunión valiosa termina sin ningún
              registro de lo que se conversó. Vimos ese mismo problema una y otra vez en
              ferias, eventos y reuniones de negocio, y decidimos construir algo distinto.
            </p>
            <p className="sn-p">
              Contact nació en Chile con una idea simple: cada contacto profesional debería
              quedar registrado, medido y accesible al instante, sin fricción y sin depender
              del papel. Hoy seguimos con el mismo objetivo, ayudar a profesionales y equipos
              comerciales a convertir cada conexión en una oportunidad real.
            </p>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="sn-values">
        <div className="sn-container">
          <p className="sn-section-label">Cómo trabajamos</p>
          <h2 className="sn-h2 sn-h2--tight">Los principios detrás de cada decisión.</h2>
          <div className="sn-values-grid">
            {VALUES.map(v => (
              <div key={v.title} className="sn-value-card">
                <h3 className="sn-value-title">{v.title}</h3>
                <p className="sn-value-desc">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOUNDERS */}
      <section className="sn-founders-section">
        <div className="sn-container">
          <p className="sn-section-label">Equipo fundador</p>
          <h2 className="sn-h2 sn-h2--tight">Construyendo Contact desde Santiago, Chile.</h2>
          <div className="sn-founders-grid">
            {FOUNDERS.map(f => (
              <div key={f.name} className="sn-founder-card">
                <div className="sn-founder-photo-wrap">
                  <img
                    src={f.photo}
                    alt={f.name}
                    className="sn-founder-photo"
                    loading="lazy"
                  />
                </div>
                <div className="sn-founder-body">
                  <span className="sn-founder-name">{f.name}</span>
                  <span className="sn-founder-role">{f.role}</span>
                  <a
                    href={f.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="sn-founder-li"
                    aria-label={`LinkedIn de ${f.name}`}
                  >
                    <IconLinkedIn/> LinkedIn
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="sn-cta">
        <div className="sn-container sn-cta-inner">
          <h2 className="sn-h2">Listo para dejar el papel atrás.</h2>
          <p className="sn-p sn-cta-sub">
            Crea tu perfil digital gratis y descubre cómo se siente no perder nunca más un
            contacto.
          </p>
          <div className="sn-cta-actions">
            <button onClick={() => navigate('/register')} className="sn-btn-pill">
              Comenzar gratis <IconArrowRight/>
            </button>
            <button onClick={() => navigate('/cotizar')} className="sn-btn-outline">
              Hablar con ventas
            </button>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="sn-footer">
        <div className="sn-container sn-footer-inner">
          <ContactLogo/>
          <span className="sn-footer-copy">© 2026 CONTACT Network SPA. Chile</span>
        </div>
      </footer>

      <style>{CSS}</style>
    </div>
  );
};

const CSS = `
:root{
  --sn-bg:#000000;
  --sn-text:#FFFFFF;
  --sn-muted:rgba(255,255,255,.65);
  --sn-faint:rgba(255,255,255,.4);
  --sn-border:rgba(255,255,255,.12);
  --sn-panel:rgba(255,255,255,.05);
  --sn-blue:#0071e3;
}

.sn-root{font-family:'Inter',system-ui,sans-serif;background:var(--sn-bg);color:var(--sn-text);min-height:100vh;overflow-x:hidden;line-height:1.6;position:relative}

/* BACKGROUND */
.sn-bg{position:fixed;inset:0;z-index:0;background:#000}

/* NAV */
.sn-nav{position:sticky;top:0;z-index:100;background:rgba(12,13,18,.72);backdrop-filter:blur(20px) saturate(180%);-webkit-backdrop-filter:blur(20px) saturate(180%);border-bottom:1px solid var(--sn-border)}
.sn-nav-inner{max-width:960px;margin:0 auto;padding:0 40px;height:64px;display:flex;align-items:center;justify-content:space-between;position:relative;z-index:1}
.sn-btn-back{display:inline-flex;align-items:center;gap:7px;background:none;border:none;color:var(--sn-muted);font-family:inherit;font-size:13px;font-weight:500;cursor:pointer;padding:0;transition:color .15s}
.sn-btn-back:hover{color:var(--sn-text)}

/* CONTAINER */
.sn-container{max-width:960px;margin:0 auto;padding:0 40px;position:relative;z-index:1}

/* HERO */
.sn-hero{padding:88px 0 64px;position:relative;z-index:1}
.sn-eyebrow{font-size:11px;font-weight:700;color:#7dd3fc;text-transform:uppercase;letter-spacing:.08em;margin-bottom:20px}
.sn-h1{font-family:-apple-system,BlinkMacSystemFont,'SF Pro Display','Inter',system-ui,sans-serif;font-size:clamp(38px,5.4vw,68px);font-weight:600;line-height:1.08;letter-spacing:-.025em;color:var(--sn-text);margin-bottom:28px;max-width:820px}
.sn-lead{font-size:clamp(16px,1.8vw,19px);color:var(--sn-muted);line-height:1.75;max-width:640px}

/* SECTION LABEL + HEADINGS */
.sn-section-label{font-size:11px;font-weight:700;color:var(--sn-text);text-transform:uppercase;letter-spacing:.08em;opacity:.5;margin-bottom:16px}
.sn-h2{font-family:-apple-system,BlinkMacSystemFont,'SF Pro Display','Inter',system-ui,sans-serif;font-size:clamp(26px,3.2vw,38px);font-weight:600;line-height:1.2;letter-spacing:-.02em;color:var(--sn-text);margin-bottom:20px;max-width:640px}
.sn-h2--tight{margin-bottom:36px}
.sn-p{font-size:16px;color:var(--sn-muted);line-height:1.75;max-width:600px;margin-bottom:18px}
.sn-p:last-child{margin-bottom:0}

/* STORY */
.sn-story{padding:0 0 96px;position:relative;z-index:1}
.sn-story-body{max-width:640px}

/* VALUES */
.sn-values{padding:0 0 96px;position:relative;z-index:1}
.sn-values-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:20px}
.sn-value-card{background:var(--sn-panel);border:1px solid var(--sn-border);border-radius:20px;padding:28px;backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px);transition:border-color .2s,transform .2s}
.sn-value-card:hover{border-color:rgba(255,255,255,.24);transform:translateY(-3px)}
.sn-value-title{font-size:16px;font-weight:700;color:var(--sn-text);margin-bottom:8px;letter-spacing:-.01em}
.sn-value-desc{font-size:14px;color:var(--sn-muted);line-height:1.65}

/* FOUNDERS SECTION */
.sn-founders-section{padding:0 0 96px;position:relative;z-index:1}
.sn-founders-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:24px;max-width:560px}
.sn-founder-card{background:var(--sn-panel);border:1px solid var(--sn-border);border-radius:20px;padding:32px 28px;display:flex;flex-direction:column;align-items:center;gap:16px;text-align:center;backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px);transition:border-color .2s,transform .2s}
.sn-founder-card:hover{border-color:rgba(255,255,255,.24);transform:translateY(-4px)}
.sn-founder-photo-wrap{width:100px;height:100px;border-radius:50%;overflow:hidden;border:2px solid var(--sn-border);flex-shrink:0}
.sn-founder-photo{width:100%;height:100%;object-fit:cover;object-position:center;display:block}
.sn-founder-body{display:flex;flex-direction:column;align-items:center;gap:4px}
.sn-founder-name{font-size:15px;font-weight:700;color:var(--sn-text)}
.sn-founder-role{font-size:12px;color:var(--sn-muted);font-weight:500;text-transform:uppercase;letter-spacing:.06em}
.sn-founder-li{display:inline-flex;align-items:center;gap:6px;padding:7px 16px;border-radius:100px;border:1.5px solid var(--sn-border);background:transparent;color:var(--sn-muted);font-family:inherit;font-size:12px;font-weight:600;text-decoration:none;transition:all .15s;margin-top:8px}
.sn-founder-li:hover{border-color:#7dd3fc;color:#7dd3fc;background:rgba(125,211,252,.08)}

/* CTA */
.sn-cta{padding:0 0 96px;position:relative;z-index:1}
.sn-cta-inner{background:var(--sn-panel);border:1px solid var(--sn-border);border-radius:28px;padding:64px 48px;text-align:center;display:flex;flex-direction:column;align-items:center;backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px)}
.sn-cta-sub{text-align:center;margin:0 auto 32px}
.sn-cta-actions{display:flex;align-items:center;gap:14px}
.sn-btn-pill{display:inline-flex;align-items:center;gap:8px;background:#fff;color:#000;border:none;border-radius:100px;height:48px;padding:0 24px;font-family:inherit;font-size:14px;font-weight:600;cursor:pointer;text-decoration:none;transition:opacity .15s,transform .2s cubic-bezier(.22,1,.36,1);white-space:nowrap}
.sn-btn-pill:hover{opacity:.85;transform:translateY(-2px)}
.sn-btn-outline{display:inline-flex;align-items:center;gap:8px;background:transparent;color:#fff;border:1.5px solid rgba(255,255,255,.3);border-radius:100px;height:48px;padding:0 24px;font-family:inherit;font-size:14px;font-weight:600;cursor:pointer;transition:all .2s;white-space:nowrap}
.sn-btn-outline:hover{border-color:#fff;background:rgba(255,255,255,.08)}

/* FOOTER */
.sn-footer{border-top:1px solid var(--sn-border);padding:24px 0;position:relative;z-index:1}
.sn-footer-inner{display:flex;align-items:center;justify-content:space-between}
.sn-footer-copy{font-size:12px;color:var(--sn-faint)}

/* RESPONSIVE */
@media(max-width:640px){
  .sn-nav-inner,.sn-container{padding:0 20px}
  .sn-hero{padding:56px 0 48px}
  .sn-story,.sn-values,.sn-founders-section,.sn-cta{padding-bottom:64px}
  .sn-values-grid{grid-template-columns:1fr}
  .sn-founders-grid{grid-template-columns:1fr;max-width:280px}
  .sn-cta-inner{padding:48px 24px}
  .sn-cta-actions{flex-direction:column;width:100%}
  .sn-cta-actions .sn-btn-pill,.sn-cta-actions .sn-btn-outline{width:100%;justify-content:center}
  .sn-footer-inner{flex-direction:column;gap:8px;text-align:center}
}
@media(prefers-reduced-motion:reduce){
  *{animation-duration:.01ms !important;transition-duration:.01ms !important}
}
`;

export default SobreNosotros;
